import { useState } from 'react'

import { Alert, Box, Icon } from '@factorialco/deprecated-design-system'
import { LegalEntityForSelectFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import TextInput from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type manualIncidenceForm from 'modules/payroll_bundle/pages/IncidencesPage/utils/manualIncidenceForm'

interface Props {
  form: manualIncidenceForm
  isEditing?: boolean
  showLegalEntitySelector?: boolean
  hasProfessionals?: boolean
}

const ManualIncidenceForm = ({
  form,
  isEditing,
  showLegalEntitySelector = true,
  hasProfessionals,
}: Props) => {
  const employeeIdField = form.get('employee_id')
  const legalEntityIdField = form.get('legal_entity_id')
  const legalEntityId = legalEntityIdField.value
  const [selectedEmployee, setSelectedEmployee] = useState<string | undefined>()
  const [selectedLegalEntity, setSelectedLegalEntity] =
    useState<LegalEntityForSelectFragment | null>(null)

  const handleSelection = (value: string | undefined) => {
    setSelectedEmployee(value)
    employeeIdField.set(value)
  }

  const handleLegalEntityChange = (legalEntity: LegalEntityForSelectFragment | null) => {
    if (legalEntity) {
      setSelectedLegalEntity(legalEntity)
      legalEntityIdField.set(legalEntity.id + '')
    }
  }

  return (
    <Box gap="s16">
      {showLegalEntitySelector && (
        <LegalEntitySelector
          label={i18n.t('bookkeepers.incidence.manual.form.labels.legal_entity')}
          value={selectedLegalEntity}
          onChange={handleLegalEntityChange}
          width="full"
        />
      )}
      <EmployeeSelectorV2
        label={i18n.t('bookkeepers.incidence.manual.form.labels.employee')}
        onChange={handleSelection}
        value={selectedEmployee}
        disabled={isEditing}
        forcedFilters={{
          legalEntityIds:
            showLegalEntitySelector && legalEntityId ? [legalEntityId] : undefined,
        }}
        options={{
          ...(!showLegalEntitySelector && {
            hideSearchIcon: true,
          }),
        }}
      />
      <TextInput
        mandatory
        label={i18n.t('bookkeepers.incidence.manual.form.labels.subject')}
        field={form.get('subject')}
      />
      <TextArea
        mandatory
        label={i18n.t('bookkeepers.incidence.manual.form.labels.description')}
        field={form.get('description')}
      />
      <DayPicker
        label={i18n.t('bookkeepers.incidence.manual.form.labels.effective_date')}
        field={form.get('effective_on')}
        minDate={moment().subtract(1, 'day')}
      />
      {!form.get('id').value && hasProfessionals && (
        <Alert color="warning">
          <Box flexDirection="row" alignItems="center" width="full">
            <Icon.Medium icon="info-circle-line" color="grey800" />
            {i18n.t('bookkeepers.incidence.manual.form.labels.todo_warning')}
          </Box>
        </Alert>
      )}
    </Box>
  )
}

export default observer(ManualIncidenceForm)
